import { UseQueryResult } from '@tanstack/react-query'
import React, { useRef } from 'react'
import { parseRequestError } from '../utilities'
import AppAlertComponent from './AppAlertComponent'
import LoadingCard from './LoadingCard'

// This type maps each query result to its data type while preserving the tuple structure
type ExtractDataFromQueries<
  TQueries extends readonly UseQueryResult<unknown, unknown>[],
> = {
  [K in keyof TQueries]: TQueries[K] extends UseQueryResult<infer Data, unknown>
    ? Data
    : never
}

interface MultiQueryPageLoaderProps<
  TQueries extends readonly UseQueryResult<unknown, unknown>[],
> {
  queries: TQueries
  children: (data: ExtractDataFromQueries<TQueries>) => React.ReactNode
  // Custom loading component (JSX)
  customLoader?: React.ReactNode
  // New prop to customize error display
  errorRenderer?: (error: unknown) => React.ReactNode
  // New prop to show loading/error only on initial fetch
  showLoadingOnlyOnFirstFetch?: boolean
}

function MultiQueryPageLoader<
  TQueries extends readonly UseQueryResult<unknown, unknown>[],
>(props: MultiQueryPageLoaderProps<TQueries>) {
  const {
    queries,
    children,
    customLoader,
    errorRenderer,
    showLoadingOnlyOnFirstFetch = false,
  } = props

  // Track if we've ever loaded data for any of the queries
  const hasEverLoadedData = useRef(false)

  // Check if any query is in initial loading state
  const isInitialLoading = queries.some((query) => query.isPending)

  // Check if any query is loading (used when not restricting to first fetch only)
  const isLoading = queries.some((query) => query.isLoading)

  // Find the first error, if any
  const firstError = queries.find((query) => query.isError)

  // Check if we have data for all queries
  const allQueriesHaveData = queries.every((query) => query.data !== undefined)

  // Update the ref if we have data
  if (allQueriesHaveData && !hasEverLoadedData.current) {
    hasEverLoadedData.current = true
  }

  // Check if we should show loading state
  // If showLoadingOnlyOnFirstFetch is true, only show loading if we've never loaded data before
  const shouldShowLoading = showLoadingOnlyOnFirstFetch
    ? isInitialLoading && !hasEverLoadedData.current
    : isLoading

  // Check if we should show error state (either it's initial error or we show errors on all fetches)
  const errorToShow = firstError

  if (shouldShowLoading) {
    // Use custom loader if provided, otherwise fall back to default LoadingCard
    if (customLoader) {
      return <>{customLoader}</>
    }
    return <LoadingCard title="Loading" />
  }

  if (errorToShow) {
    // Use custom error renderer if provided, otherwise fall back to default error component
    if (errorRenderer) {
      return <>{errorRenderer(errorToShow.error)}</>
    }
    return (
      <div className="max-w-xl mx-auto">
        <AppAlertComponent
          variant="error"
          title="Error"
          text={parseRequestError(errorToShow.error).message}
        >
          {null}
        </AppAlertComponent>
      </div>
    )
  }

  // For non-initial fetches when showLoadingOnlyOnFirstFetch is true,
  // we should have data we can use even if there's loading or error
  if (showLoadingOnlyOnFirstFetch && allQueriesHaveData) {
    // Extract data from all queries with proper typing
    const data = queries.map(
      (query) => query.data
    ) as unknown as ExtractDataFromQueries<TQueries>
    return <>{children(data)}</>
  }

  // Normal case - all queries succeeded
  const data = queries.map(
    (query) => query.data
  ) as unknown as ExtractDataFromQueries<TQueries>
  return <>{children(data)}</>
}

export default MultiQueryPageLoader
